import React, { useEffect, useState } from 'react';
import { Box, Typography, IconButton, Chip, Link as MuiLink } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Link from 'next/link';
import { sendApiRequest } from 'lib/api/public';
import notify from 'lib/notify';
import { sanitizeHtml } from '../lib/sanitizeHtml';
import { IAttachment } from './types';

export type IChangelog = {
  _id: string;
  version: string;
  releaseDate: string;
  content?: string;
  shortDescription?: string;
  createdAt?: Date;
  updatedAt?: Date;
  images?: IAttachment[];
};

const ChangelogAnnouncement: React.FC = () => {
  const [changelogs, setChangelogs] = useState<IChangelog[] | null>(null);

  const getLatestChangelog = async () => {
    try {
      const response = (await sendApiRequest({
        path: '/changelog/latest',
        method: 'GET',
      })) as { changelogs: IChangelog[] | null };

      setChangelogs(response.changelogs);
    } catch (error: unknown) {
      notify(error as Error);
    }
  };

  const markChangelogAsRead = async (changelogId: string) => {
    try {
      await sendApiRequest({
        path: '/changelog/mark-as-read',
        method: 'POST',
        data: { changelogId },
      });

      setChangelogs(prev => (prev || []).filter(c => c._id !== changelogId));
    } catch (error: unknown) {
      notify(error as Error);
    }
  };

  useEffect(() => {
    void getLatestChangelog();
  }, []);

  if (!changelogs || changelogs.length === 0) {
    return null;
  }
  return (
    <Box sx={{ mb: 3 }}>
      {changelogs.map(changelog => (
        <Box
          key={changelog._id}
          sx={{
            backgroundColor: 'primary.main',
            border: '1px solid',
            borderColor: 'primary.main',
            borderRadius: 3,
            boxShadow: '0 4px 12px rgba(30, 58, 138, 0.15)',
            padding: 3,
            position: 'relative',
            mb: 2,
          }}
        >
          <IconButton
            aria-label="close"
            color="inherit"
            size="small"
            onClick={() => void markChangelogAsRead(changelog._id)}
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              color: '#ffffff',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <CloseIcon fontSize="inherit" />
          </IconButton>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Chip
              label="New"
              size="small"
              sx={{
                backgroundColor: '#ffffff',
                color: 'primary.main',
                fontSize: '0.75rem',
                fontWeight: 'bold',
                height: '24px',
              }}
            />
            <Link href="/changelog" passHref legacyBehavior>
              <MuiLink
                sx={{
                  fontWeight: 'bold',
                  color: '#ffffff',
                  fontSize: '1.1rem',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  '&:hover': {
                    color: '#ffffff',
                    textDecoration: 'underline',
                  },
                }}
              >
                <Typography
                  variant="h6"
                  component="span"
                  sx={{ fontWeight: 'bold', color: '#ffffff', fontSize: '1.1rem' }}
                >
                  {changelog.shortDescription}
                </Typography>
              </MuiLink>
            </Link>
          </Box>
          <Box
            sx={{
              color: '#ffffff',
              '& h1, & h2, & h3, & h4, & h5, & h6': {
                color: '#ffffff',
                marginBottom: 1,
              },
              '& p': {
                marginBottom: 1,
                lineHeight: 1.6,
              },
              '& ul, & ol': {
                marginBottom: 1,
                paddingLeft: 2,
              },
              '& li': {
                marginBottom: 0.5,
              },
              '& a': {
                color: '#93c5fd',
                textDecoration: 'underline',
                '&:hover': {
                  color: '#dbeafe',
                },
              },
            }}
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(changelog.content ?? '') }}
          />
          <Box sx={{ mt: 2 }}>
            <Link href="/changelog" passHref legacyBehavior>
              <MuiLink
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: '#ffffff',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  transition: 'color 0.2s ease-in-out',
                  '&:hover': {
                    color: '#ffffff',
                    textDecoration: 'underline',
                  },
                  '&:active': {
                    color: '#ffffff',
                  },
                  '&:visited': {
                    color: '#ffffff',
                  },
                  '&:focus': {
                    color: '#ffffff',
                    outline: '2px solid #ffffff',
                    outlineOffset: '2px',
                  },
                }}
              >
                Learn more
              </MuiLink>
            </Link>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default ChangelogAnnouncement;
